// Walkthrough — a first-launch interactive onboarding overlay that guides
// new users through Holy Words' core features. Shows ONCE automatically
// (persisted in localStorage), and can be replayed any time from
// Settings via startWalkthrough().
//
// Why: Google Play reviewers flagged the missing new-user tutorial. A
// short, skippable tour improves first-run comprehension and retention.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen, MicrophoneStage, Sparkle, Baby, Sun, Fire,
  ArrowLeft, ArrowRight, X, Check,
} from "@phosphor-icons/react";
import Tr from "./Tr";

// Bump the version to re-show the walkthrough to everyone after a big
// feature change.
const WALKTHROUGH_VERSION = "v1-2026-06";
const SEEN_KEY = `hw_walkthrough_seen_${WALKTHROUGH_VERSION}`;
const EVENT = "hw:walkthrough:start";

// Imperatively (re)open the walkthrough — used by the Settings "Replay
// walkthrough" button.
export function startWalkthrough() {
  try {
    window.dispatchEvent(new CustomEvent(EVENT));
  } catch (_) {}
}

const STEPS = [
  {
    icon: BookOpen,
    accent: "from-forest to-forest-600",
    badge: "Welcome",
    title: "Welcome to Holy Words",
    body: "Your free home for Scripture, sermons, gospel music, and faith films — read, listen, and grow every day.",
    cta: null,
  },
  {
    icon: MicrophoneStage,
    accent: "from-terracotta to-terracotta-600",
    badge: "Read & Listen",
    title: "The Bible, read aloud",
    body: "You can pick between 18 different languages to have the Bible read aloud to you — open any chapter, tap play, and follow along with verse-by-verse highlighting.",
    cta: { label: "Open the Bible", to: "/read" },
  },
  {
    icon: Sparkle,
    accent: "from-amber-500 to-amber-600",
    badge: "AI Companion",
    title: "Ask your AI faith companion",
    body: "Have a question about a verse or need a prayer? The AI Companion gives thoughtful, Scripture-grounded answers and prays with you any time.",
    cta: { label: "Try the Companion", to: "/my-prayers" },
  },
  {
    icon: Sun,
    accent: "from-forest to-emerald-700",
    badge: "Daily Devotional",
    title: "A devotional every morning",
    body: "Start each day with a verse and reflection, plus the nightly Lord's Prayer — delivered right when you want it.",
    cta: { label: "See today's devotional", to: "/" },
  },
  {
    icon: Fire,
    accent: "from-rose-500 to-terracotta-600",
    badge: "Discover",
    title: "Trending & Kids' Corner",
    body: "Swipe Trending for the most-loved clips, and explore Kids' Corner — animated Bible stories made for little ones.",
    cta: { label: "Explore Trending", to: "/discover" },
  },
];

export default function Walkthrough() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  // Auto-show once per device on first launch — but never on the
  // /login or /register screens, where it would cover the Sign In button.
  useEffect(() => {
    let seen = true;
    try { seen = !!window.localStorage.getItem(SEEN_KEY); } catch (_) {}
    const path = (typeof window !== "undefined" && window.location && window.location.pathname) || "";
    const onAuthScreen = path.startsWith("/login") || path.startsWith("/register") || path.startsWith("/signup")
      || path.startsWith("/tour"); // cinematic tour has its own narration — don't stack popups
    if (!seen && !onAuthScreen) {
      // Small delay so the app shell paints first.
      const t = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  // Allow Settings (or anywhere) to replay the tour.
  useEffect(() => {
    const onStart = () => { setStep(0); setOpen(true); };
    window.addEventListener(EVENT, onStart);
    return () => window.removeEventListener(EVENT, onStart);
  }, []);

  const finish = useCallback(() => {
    try { window.localStorage.setItem(SEEN_KEY, "1"); } catch (_) {}
    setOpen(false);
  }, []);

  const goTo = (cta) => {
    finish();
    if (cta?.to) navigate(cta.to);
  };

  if (!open) return null;

  const s = STEPS[step];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-ink/70 backdrop-blur-sm animate-fade-up"
      data-testid="walkthrough-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full sm:max-w-md bg-cream-50 rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl">
        {/* Skip */}
        <button
          type="button"
          onClick={finish}
          data-testid="walkthrough-skip"
          className="absolute top-4 right-4 z-10 w-9 h-9 grid place-items-center rounded-full bg-white/70 text-ink/60 hover:text-ink transition-colors"
          aria-label="Skip walkthrough"
        >
          <X size={18} weight="bold" />
        </button>

        {/* Hero band */}
        <div className={`bg-gradient-to-br ${s.accent} px-7 pt-10 pb-8 text-cream`}>
          <span className="inline-block text-[10px] tracking-[0.25em] uppercase font-bold bg-white/20 rounded-full px-3 py-1">
            <Tr eager>{s.badge}</Tr>
          </span>
          <div className="mt-5">
            <Icon size={44} weight="duotone" />
          </div>
          <h2 className="font-heading text-3xl mt-4 leading-tight" data-testid="walkthrough-title">
            <Tr eager>{s.title}</Tr>
          </h2>
        </div>

        {/* Body */}
        <div className="px-7 py-6">
          <p className="text-ink/80 text-sm leading-relaxed" data-testid="walkthrough-body">
            <Tr eager>{s.body}</Tr>
          </p>

          {s.cta && (
            <button
              type="button"
              onClick={() => goTo(s.cta)}
              data-testid="walkthrough-cta"
              className="mt-4 inline-flex items-center gap-1.5 text-terracotta hover:underline text-sm font-semibold"
            >
              <Tr eager>{s.cta.label}</Tr> <ArrowRight size={14} weight="bold" />
            </button>
          )}

          {/* Progress dots */}
          <div className="mt-7 flex items-center justify-center gap-2" data-testid="walkthrough-dots">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? "w-6 bg-forest" : "w-1.5 bg-cream-300"
                }`}
              />
            ))}
          </div>

          {/* Nav */}
          <div className="mt-6 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setStep((v) => Math.max(0, v - 1))}
              disabled={step === 0}
              data-testid="walkthrough-back"
              className="inline-flex items-center gap-1.5 px-4 h-11 rounded-full text-ink/70 hover:text-ink text-sm font-semibold disabled:opacity-0 disabled:pointer-events-none transition"
            >
              <ArrowLeft size={16} weight="bold" /> <Tr eager>Back</Tr>
            </button>

            {isLast ? (
              <button
                type="button"
                onClick={finish}
                data-testid="walkthrough-finish"
                className="inline-flex items-center gap-2 px-6 h-11 rounded-full bg-forest text-cream text-sm font-bold hover:bg-forest-600 transition active:scale-95"
              >
                <Check size={16} weight="bold" /> <Tr eager>Get started</Tr>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setStep((v) => Math.min(STEPS.length - 1, v + 1))}
                data-testid="walkthrough-next"
                className="inline-flex items-center gap-2 px-6 h-11 rounded-full bg-forest text-cream text-sm font-bold hover:bg-forest-600 transition active:scale-95"
              >
                <Tr eager>Next</Tr> <ArrowRight size={16} weight="bold" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
